import { tokens } from '../storage/storage';
import { ApiError } from './apiError';
import type { Query } from '../../types/api';

export const BASE = (process.env.REACT_APP_API_URL || '/api/v1').replace(/\/$/, '');

function buildUrl(path: string, query?: Query) {
  const params = new URLSearchParams();
  Object.entries(query || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  });
  const qs = params.toString();
  return `${BASE}${path}${qs ? `?${qs}` : ''}`;
}

let refreshing: Promise<boolean> | null = null;
function refreshTokens(): Promise<boolean> {
  const { refreshToken } = tokens.get();
  if (!refreshToken) return Promise.resolve(false);
  if (!refreshing) {
    refreshing = fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    })
      .then(async (r) => {
        if (!r.ok) return false;
        const { data } = await r.json();
        tokens.set({ accessToken: data.accessToken, refreshToken: data.refreshToken });
        return true;
      })
      .catch(() => false)
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

const NO_REFRESH = ['/auth/login', '/auth/refresh'];

interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Query;
  raw?: boolean;
  retry?: boolean;
}

export async function request(path: string, opts: RequestOptions & { raw: true }): Promise<Response>;
export async function request<T = any>(path: string, opts?: RequestOptions): Promise<T>;
export async function request(path: string, { method = 'GET', body, query, raw = false, retry = true }: RequestOptions = {}): Promise<any> {
  const { accessToken } = tokens.get();
  let res: Response;
  try {
    res = await fetch(buildUrl(path, query), {
      method,
      headers: {
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError('No internet connection. Check your network and try again.', 0);
  }

  if (res.status === 401 && retry && !NO_REFRESH.includes(path)) {
    if (await refreshTokens()) return request(path, { method, body, query, raw, retry: false });
    tokens.clear();
    window.dispatchEvent(new Event('auth:logout'));
  }

  if (raw && res.ok) return res;
  const json = await res.json().catch(() => null);
  if (!res.ok) {
    // Errors: { statusMessage, status, message, details? } (older backends sent { error: { message, details } }).
    throw new ApiError(json?.message ?? json?.error?.message ?? `Request failed (${res.status})`, res.status, json?.details ?? json?.error?.details);
  }
  return json;
}

// GETs still waiting for an answer, by URL. A second identical GET while the first is
// in flight shares its result instead of hitting the server again (React StrictMode runs
// effects twice in development; two cards on one screen may load the same list).
const inFlight = new Map<string, Promise<any>>();

function sharedGet<T>(path: string, query?: Query): Promise<T> {
  const key = buildUrl(path, query);
  const pending = inFlight.get(key);
  if (pending) return pending;
  const p = request<T>(path, { query }).finally(() => inFlight.delete(key));
  inFlight.set(key, p);
  return p;
}

/**
 * Thin JSON client. Responses are `{ statusMessage, status, message, data }` (plus `meta` for lists).
 * The type parameter is the whole response body, e.g. api.get<ApiResponse<Customer>>(...).
 */
export const api = {
  get: <T = any>(path: string, query?: Query) => sharedGet<T>(path, query),
  post: <T = any>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body: body ?? {} }),
  put: <T = any>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T = any>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  del: <T = any>(path: string) => request<T>(path, { method: 'DELETE' }),
  /** Opens a PDF endpoint (e.g. /invoices/:id/pdf) in a new tab. */
  async openPdf(path: string) {
    const res = await request(path, { raw: true });
    const url = URL.createObjectURL(await res.blob());
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },
};
